/*  MUI  */
import {
    Box,
    Card,
    CardContent,
    FormControlLabel,
    Grid,
    Paper,
    TableCell,
    Typography
} from '@mui/material';
import { UserGreeting } from 'application/Shared/Components/App/UserGreeting';

/*  Shared  */
import { LastUpdatedCard } from 'application/Shared/Components/Cards/Components/LastUpdatedCard';
import { Mock } from 'application/Shared/Components/Core';
import { ReportWrapper } from 'application/Shared/Components/Reports';
import { ShowPartnerTopInfoBanner } from 'application/Shared/Components/ShowPartnerTopInfoBanner';
import { AuvikSwitch } from 'application/Shared/Components/Switches';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Browser, Storage } from 'application/Shared/Handlers';
import {
    Helpers,
    Response,
    Route,
    SessionHelper,
    URL
} from 'application/Shared/Helpers';
import { formatDistanceToNow } from 'application/Shared/Helpers/Dates';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import { Application } from 'objects/Application';
import { Breach, BreachDetail } from 'objects/Breach';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { withRouter } from 'router/withRouter';

/*  Constants  */
const { hasData, getData, getDistinct } = Response;

const headers = [
    {
        renderHeader: i18n.t('Common.client'),
        width: '40%'
    },
    {
        renderHeader: i18n.t('Common.dateAdded'),
        width: '20%'
    },
    {
        renderHeader: i18n.t('Common.active_users'),
        width: '20%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        width: '20%'
    }
];

const searchFieldsBreaches = ['description'];
const searchFieldsClients = ['name', 'description', 'category', 'baseName'];

class Dashboard extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            activeUsers: 0,
            appCount: 0,
            clients: [],
            excludeInternal: false,
            includePartner: false,
            loaded: false,
            onlyShowImpactedClients: true,
            parameters: {},
            redirect: false,
            startTime: new Date().getTime() - 60 * 60 * 24 * 7 * 1000,
            to: ''
        };

        /* requsts */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = async () => {
        const { session, location, match, tenants } = this.props;
        const { partnerId } = match.params;

        const searchParams = URL.getParams(location);

        if (searchParams.has('clientId')) {
            const clientId = searchParams.get('clientId');
            const applicationId = searchParams.get('applicationId');
            const reportParams = searchParams.get('reportParams');

            // TODO: WTF is this? Do we still need this or can we remove it?
            // Pretty sure this is here to support redirecting the scheduled reports cron to the reports page.
            // That cron has been updated in https://gitlab.com/auviknetworks/saaslio/cron/-/merge_requests/80
            // to just go directly to the correct reports path, so this can likely be removed.
            this.handleLogin({
                applicationId,
                clientId,
                partnerId,
                reportParams
            });
            return;
        }
        const partnerTenantInfo = tenants[partnerId];
        const partnerTenant = partnerTenantInfo?.tenant;
        await this.handleSession(session, partnerTenant);
        this.loadClients();
        this.loadAppCount();

        const time = new Date().getTime() - 60 * 60 * 1000 * 24 * 365 * 2;
        const parameters = {
            dateAnnounced: `{"gt": "${new Date(time).toISOString()}"}`,
            includeApplication: true,
            includeClientsImpacted: true,
            includeOnlyImpactedClients: true,
            size: -1
        };

        if (!this.unmount) {
            this.setState({ loaded: true, parameters });
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    handleSession = async (session, partnerTenant) => {
        if (this.unmount) {
            return;
        }
        const sessionHelper = new SessionHelper(session);

        // noInternalAccess should is based on the current session, not the tenant being viewed.
        const excludeInternal = sessionHelper.noInternalAccess();

        // If we're not viewing the session's tenant, use the tenant data from redux.
        if (partnerTenant && partnerTenant.id !== sessionHelper.getTenantId()) {
            const { id, internalId } = partnerTenant;
            const includePartner = id === internalId;

            this.setState({
                excludeInternal,
                includePartner
            });

            return;
        }

        const partner = sessionHelper.getTenant();
        const { id, internalId } = partner;

        const includePartner = id === internalId;

        this.setState({
            excludeInternal,
            includePartner
        });
    };

    // TODO: Do we need this either?
    handleLogin = async (p) => {
        if (this.unmount) {
            return;
        }

        const { session } = this.props;
        const { clientId, partnerId, applicationId, reportParams } = p;

        // Determine the page to redirect to after Mock
        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });
        let to = baseAdminRoute;
        if (applicationId) {
            to = `${baseAdminRoute}/application/${applicationId}/details`;
        }

        if (reportParams) {
            const baseReportsRoute = Route.buildBaseReportsRoute({
                clientId,
                partnerId
            });
            to = `${baseReportsRoute}?reportParams=${reportParams}`;
        }

        // Check to see if the Client is Internal Admin
        const sessionHelper = new SessionHelper(session);
        if (sessionHelper.getTenantId() === clientId) {
            this.setState({ redirect: true, to });
            return;
        }

        const tenant = sessionHelper.getTenant();

        Browser.handleTabTitle({ tenant });
        this.setState({ redirect: true, to });
    };

    getActiveUsers = (clients) => {
        if (this.unmount) {
            return;
        }

        let activeUsers = 0;
        clients.forEach((client) => {
            const { tenant } = client;
            if (!tenant) {
                return;
            }

            const { userCount } = tenant;
            activeUsers += parseInt(userCount, 10);
        });
        this.setState({ activeUsers });
    };

    loadClients = async () => {
        if (this.unmount) {
            return;
        }

        const { includePartner, excludeInternal } = this.state;

        const { match } = this.props;
        const { partnerId } = match.params;
        try {
            const onboardingState = {
                redirect: true,
                to: `/partner/${partnerId}/onboarding`
            };

            const exitedOnboarding = await Storage.getWithExpiry(
                'exitedOnboarding'
            );

            const hasExitedOnboarding = exitedOnboarding === true;

            const response = await axios.get('/clientLink', {
                cancelToken: this.requests.token(),
                params: {
                    excludeInternal,
                    hidden: false,
                    includePartner,
                    includeTenant: true,
                    includeUserCount: true,
                    size: -1
                }
            });

            if (!hasData(response)) {
                if (!hasExitedOnboarding) {
                    this.setState(onboardingState);
                }
                return;
            }

            const clients = getData(response);

            if (!hasExitedOnboarding && !clients?.length) {
                this.setState(onboardingState);
            }

            this.getActiveUsers(clients);
            this.setState({ clients });
        } catch (e) {
            debug.e('Unable to load Clients', e);
        }
    };

    loadAppCount = async () => {
        if (this.unmount) {
            return;
        }

        try {
            const response = await axios.get('/tenantApp', {
                cancelToken: this.requests.token(),
                params: {
                    applicationId: '*',
                    excludePersonal: true,
                    includeClients: true,
                    size: -1
                }
            });
            if (!hasData(response)) {
                debug.w('No Apps found', response);
                return;
            }

            const appIds = getDistinct(response, 'applicationId');
            this.setState({ appCount: Helpers.count(appIds) });
        } catch (e) {
            debug.e('Load App Count Error', e);
        }
    };

    renderCounts = () => {
        const { t, match } = this.props;
        const { clients, activeUsers, appCount } = this.state;
        const { partnerId } = match.params;
        return (
            <>
                <Grid item xs={4} alignItems="stretch">
                    <LastUpdatedCard
                        content={clients.length}
                        lastUpdated={t('Common.just_updated')}
                        route={`/partner/${partnerId}/organizations`}
                        buttonText={t('Common.manage_clients')}
                        title={t('Common.clients')}
                    />
                </Grid>
                <Grid item xs={4}>
                    <LastUpdatedCard
                        content={activeUsers}
                        lastUpdated={t('Common.just_updated')}
                        route={`/partner/${partnerId}/manageUsers/users`}
                        buttonText={t('Common.manage_users')}
                        title={t('Common.active_users')}
                    />
                </Grid>
                <Grid item xs={4}>
                    <LastUpdatedCard
                        content={appCount}
                        lastUpdated={t('Common.just_updated')}
                        route={`/partner/${partnerId}/applicationSettings`}
                        buttonText={t('Common.manage_applications')}
                        title={t('Common.applications')}
                    />
                </Grid>
            </>
        );
    };

    renderDeploymentStage = () => {
        const { t } = this.props;
        const { startTime } = this.state;

        return (
            <>
                <Grid item xs={12} lg={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h5">
                                {t('Common.client_stages_breakdown')}
                            </Typography>
                            <hr />
                            <ReportWrapper
                                startTime={startTime}
                                period="weekly"
                                reportSelection="ClientStages"
                                type="partnerClientDeploymentStage"
                                reportCheckbox="Breakdown"
                                applicationOrCategorySelection="application"
                                applicationSelection={[]}
                                userOrGroupSelection="all"
                                height={128}
                                noLegend
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} lg={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h5">
                                {t('Common.client_stages_overview_breakdown')}
                            </Typography>
                            <hr />
                            <ReportWrapper
                                startTime={startTime}
                                period="weekly"
                                reportSelection="ClientStages"
                                type="partnerClientDeploymentStage"
                                reportCheckbox="OverviewBreakdown"
                                applicationOrCategorySelection="application"
                                applicationSelection={[]}
                                userOrGroupSelection="all"
                                height={128}
                                noLegend
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </>
        );
    };

    handleFilterVendorIncidents = ({ value }) => {
        const { parameters } = this.state;
        this.setState({
            onlyShowImpactedClients: value,
            parameters: { ...parameters, includeOnlyImpactedClients: value }
        });
    };

    renderBreaches = () => {
        const { t } = this.props;
        const { onlyShowImpactedClients, parameters } = this.state;

        return (
            <Card variant="outlined" style={{ minHeight: '1170px' }}>
                <CardContent>
                    <Typography variant="h4">
                        {t('VendorIncident.recent')}
                    </Typography>
                    <Box mb="8px" mt="16px">
                        <FormControlLabel
                            control={
                                <Box>
                                    <AuvikSwitch
                                        checked={onlyShowImpactedClients}
                                        onChange={(_, value) =>
                                            this.handleFilterVendorIncidents({
                                                value
                                            })
                                        }
                                        inputProps={{
                                            'aria-label': 'controlled'
                                        }}
                                    />
                                </Box>
                            }
                            label={
                                <Typography
                                    variant="p"
                                    style={{ marginLeft: '8px' }}
                                >
                                    {t('VendorIncident.onlyImpactedClients')}
                                </Typography>
                            }
                            style={{ marginLeft: 0 }}
                        />
                    </Box>
                    <ManagedTable
                        alternateRows
                        parameters={parameters}
                        renderBody={(row) => (
                            <TableCell>
                                <div
                                    style={{
                                        marginBottom: '16px',
                                        marginTop: '4px',
                                        minHeight: '185px'
                                    }}
                                >
                                    <Breach data={row}>
                                        <Application data={row.application}>
                                            <BreachDetail />
                                        </Application>
                                    </Breach>
                                </div>
                            </TableCell>
                        )}
                        renderBodyKey="id"
                        renderFooter
                        renderHeaderRow={false}
                        rowsApi="/breach"
                        rowsApiPreload
                        rowsPerPage={5}
                        searchEnabled
                        searchFields={searchFieldsBreaches}
                        sortDirection="DESC"
                        sortFieldType="date"
                        sortKey="dateAnnounced"
                    />
                </CardContent>
            </Card>
        );
    };

    renderClientRow = (row) => {
        const { t } = this.props;
        const { id, platformTenantId, tenant } = row;

        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId: tenant.id,
            partnerId: platformTenantId
        });

        return (
            <>
                <TableCell>
                    <Link className="link underline" to={baseAdminRoute}>
                        {tenant.name}
                    </Link>
                </TableCell>
                <TableCell align="left">
                    {formatDistanceToNow(tenant.dateCreated)}
                </TableCell>
                <TableCell>{tenant.userCount}</TableCell>
                <TableCell align="right">
                    <Mock id={tenant.id} linkId={id} button>
                        {t('Common.view_client')}
                    </Mock>
                </TableCell>
            </>
        );
    };

    renderClients = () => {
        const { t } = this.props;
        const { clients } = this.state;

        return (
            <Paper elevation={0} variant="outlined">
                <Typography variant="h4" pb="8px">
                    {t('Common.recently_added_clients')}
                </Typography>
                <ManagedTable
                    headers={headers}
                    noEmptyRows
                    renderBody={this.renderClientRow}
                    renderBodyKey="id"
                    renderFooter
                    rows={clients}
                    rowsPerPage={5}
                    searchEnabled={false}
                    searchFields={searchFieldsClients}
                    sortDirection="DESC"
                    sortFieldType="date"
                    sortKey="dateCreated"
                />
            </Paper>
        );
    };

    render() {
        const { loaded, redirect, to } = this.state;

        if (redirect) {
            return <Navigate to={`${to}`} replace />;
        }

        if (!loaded) {
            return null;
        }

        return (
            <>
                <ShowPartnerTopInfoBanner type="getStarted" />
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <UserGreeting />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Grid container spacing={3}>
                            {this.renderCounts()}
                            {this.renderDeploymentStage()}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        {this.renderBreaches()}
                    </Grid>
                    <Grid item xs={12}>
                        {this.renderClients()}
                    </Grid>
                </Grid>
            </>
        );
    }
}
export default withRouter(withTranslation()(Dashboard));
